import { memo } from 'react';
import { sameNodeContent } from '../../Services/GraphRenderState';
import { Node, NodeProps, NodeResizer, NodeToolbar, Position, useReactFlow } from '@xyflow/react';

import { useCallback, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { useCopyToClipboard, useKeyDown } from '@@common';
import { ConfirmDialog } from '@@components';

import { useFlowMetadata } from '../../Hooks/Metadata';
import { ActivityModel } from '../../Models/FlowModel';
import { FlowEdge, FlowNode } from '../../Models/GraphModel';
import './SectionNode.scss';
import { resolveMuiColor } from './colorUtils';

export const SECTION_NODE_TYPE = 'section';

const DEFAULT_SECTION_WIDTH = 320;
const DEFAULT_SECTION_HEIGHT = 240;

function SectionNode({ data, selected, id, width, height }: NodeProps & { data: ActivityModel }) {
	const { getNodes, getEdges, setNodes, setEdges } = useReactFlow<FlowNode, FlowEdge>();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const { clipboardService } = useFlowMetadata();
	const copyToClipboard = useCopyToClipboard();
	const theme = useTheme();

	const handleCopyData = useCallback(async () => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const allEdges = getEdges();
		const model = clipboardService.buildClipboardModel(id, allNodes, allEdges);
		if (!model) return;
		await copyToClipboard(model);
	}, [id, clipboardService, getNodes, getEdges, copyToClipboard]);

	const handleClone = useCallback(() => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const allEdges = getEdges();
		const { nodes: clonedNodes, edges: clonedEdges } = clipboardService.buildCloneResult(id, allNodes, allEdges);
		if (clonedNodes.length === 0) return;
		setNodes((nodes) => [...nodes, ...clonedNodes]);
		if (clonedEdges.length > 0) setEdges((edges) => [...edges, ...clonedEdges]);
	}, [id, clipboardService, getNodes, getEdges, setNodes, setEdges]);

	const handleRemove = useCallback(() => {
		const allNodes = getNodes() as Node<ActivityModel>[];
		const idsToRemove = clipboardService.collectSubtreeIds(id, allNodes);
		setEdges((edges) => edges.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target)));
		setNodes((nodes) => nodes.filter((n) => !idsToRemove.has(n.id)));
		setShowDeleteConfirm(false);
	}, [id, clipboardService, getNodes, setEdges, setNodes]);

	useKeyDown(
		['Delete', 'Backspace'],
		(event) => {
			const target = event.target as HTMLElement;
			const isReactFlowPane = target.classList.contains('react-flow__pane') || !!target.closest('.react-flow__pane');
			const isThisNode = !!target.closest('.section-node');
			if (!isReactFlowPane && !isThisNode) return;
			event.preventDefault();
			setShowDeleteConfirm(true);
		},
		selected,
	);

	useKeyDown(
		['c'],
		(event) => {
			if (!event.ctrlKey && !event.metaKey) return;
			const target = event.target as HTMLElement;
			const isInputField =
				target.tagName === 'INPUT' ||
				target.tagName === 'TEXTAREA' ||
				target.isContentEditable ||
				!!target.closest('input, textarea, [contenteditable="true"]');
			if (isInputField) return;
			if (!target.closest('.react-flow')) return;
			event.preventDefault();
			handleCopyData();
		},
		selected,
	);

	const label = (data as ActivityModel & { Label?: string }).Label;
	const color = resolveMuiColor((data as ActivityModel & { Color?: string }).Color, theme);

	return (
		<>
			<NodeResizer minWidth={160} minHeight={120} isVisible={selected} />
			<NodeToolbar position={Position.Bottom}>
				<Stack direction="row" spacing={1}>
					<IconButton onClick={handleClone} size="small" color="warning" title="Clone" sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={handleCopyData}
						size="small"
						color="primary"
						title="Copy Data as JSON"
						sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}
					>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => setShowDeleteConfirm(true)}
						size="small"
						color="error"
						title="Remove"
						sx={{ '&:hover': { backgroundColor: theme.palette.background.paper } }}
					>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</Stack>
			</NodeToolbar>
			<div
				className={`section-node${selected ? ' selected' : ''}`}
				style={{
					width: width ?? DEFAULT_SECTION_WIDTH,
					height: height ?? DEFAULT_SECTION_HEIGHT,
					...(color && {
						borderColor: `${color}59`,
						background: `${color}0a`,
					}),
				}}
			>
				{label && (
					<div className="section-node__label" style={color ? { color: `${color}d9`, background: `${color}26`, borderColor: `${color}40` } : undefined}>
						{label}
					</div>
				)}
			</div>
			<ConfirmDialog
				open={showDeleteConfirm}
				title="Delete Section"
				message="Are you sure you want to delete this section?"
				onConfirm={handleRemove}
				onCancel={() => setShowDeleteConfirm(false)}
			/>
		</>
	);
}

export function nodeFromSection(model: ActivityModel, x: number = 0, y: number = 0): Node<ActivityModel> {
	return {
		id: model.Id,
		type: SECTION_NODE_TYPE,
		position: { x, y },
		data: model,
		width: (model.Metadata?.width as number) ?? DEFAULT_SECTION_WIDTH,
		height: (model.Metadata?.height as number) ?? DEFAULT_SECTION_HEIGHT,
		zIndex: -1,
	} as Node<ActivityModel>;
}

export default memo(SectionNode, sameNodeContent);
